import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Footprints, AlertTriangle, CheckCircle2, ClipboardCheck } from 'lucide-react';
import { fetchCpcKpis } from '@/services/api';

const BarList = ({ items, total, color = '#4A9EFF', emptyLabel = 'Sin datos' }) => {
  if (!items || items.length === 0) {
    return <p className="text-[11px]" style={{ color: 'var(--vz-text-muted)' }}>{emptyLabel}</p>;
  }
  return (
    <div className="space-y-1.5">
      {items.map((item) => {
        const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
        return (
          <div key={item.value} className="flex items-center gap-3 py-0.5">
            <div className="flex-1 min-w-0">
              <p className="text-xs truncate" style={{ color: 'var(--vz-text-primary)' }}>{item.label}</p>
              <div className="h-1.5 mt-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
              </div>
            </div>
            <div className="flex-shrink-0 text-right">
              <span className="text-xs font-mono font-semibold" style={{ color }}>{item.count}</span>
              <span className="text-[10px] ml-1.5" style={{ color: 'var(--vz-text-muted)' }}>({pct}%)</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const StatPill = ({ label, value, color, icon: Icon, sub }) => (
  <div className="rounded-xl border p-3" style={{ background: 'var(--vz-bg-elevated, rgba(255,255,255,0.02))', borderColor: 'var(--vz-border-subtle)' }}>
    <div className="flex items-center gap-2">
      {Icon && <Icon size={14} style={{ color }} />}
      <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>{label}</span>
    </div>
    <p className="text-xl font-bold mt-1 font-mono" style={{ color }}>{value}</p>
    {sub && <p className="text-[10px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>{sub}</p>}
  </div>
);

const Block = ({ title, children }) => (
  <div>
    <h4 className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--vz-text-secondary)' }}>{title}</h4>
    {children}
  </div>
);

export const CadaPasoPanel = ({ filters = {}, enabled = true }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['ops-cpc', filters],
    queryFn: () => fetchCpcKpis(filters),
    enabled,
    staleTime: 60000,
  });

  const total = data?.total ?? 0;

  return (
    <div className="rounded-2xl border p-4 space-y-4" style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }} data-testid="ops-cpc-panel">
      <div className="flex items-center gap-2">
        <Footprints size={16} style={{ color: '#26C6DA' }} />
        <div>
          <h3 className="font-montserrat text-xs font-bold uppercase tracking-[0.16em]" style={{ color: 'var(--vz-text-secondary)' }}>
            Cada paso cuenta
          </h3>
          <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>Observación de tropiezos, entorno y conducta en desplazamientos</p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-xs py-6 text-center" style={{ color: 'var(--vz-text-muted)' }}>Cargando…</p>
      ) : total === 0 ? (
        <p className="text-xs py-6 text-center" style={{ color: 'var(--vz-text-muted)' }} data-testid="ops-cpc-empty">
          Aún no hay observaciones de "Cada paso cuenta" en el periodo/filtro seleccionado.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ops-cpc-stats">
            <StatPill label="Observaciones" value={total} color="#26C6DA" icon={ClipboardCheck} />
            <StatPill label="Con tropiezo" value={`${data.pct_tropiezo}%`} sub={`${data.con_tropiezo} casos`} color="#FF5252" icon={AlertTriangle} />
            <StatPill label="Conducta segura" value={`${data.conducta?.pct_global ?? 0}%`} sub="Mira dónde pisa · zona · EPIs" color="#00C853" icon={CheckCircle2} />
            <StatPill label="Requieren seguimiento" value={data.seguimiento_pendiente} color="#FFC107" icon={AlertTriangle} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4 pt-1">
            <Block title="Causas del tropiezo"><BarList items={data.causas} total={total} color="#FF5252" emptyLabel="Sin tropiezos registrados" /></Block>
            <Block title="Acción inmediata tomada"><BarList items={data.acciones} total={total} color="#AB47BC" emptyLabel="Sin acciones registradas" /></Block>
            <Block title="Estado del suelo"><BarList items={data.entorno?.suelo} total={total} color="#4A9EFF" /></Block>
            <Block title="Condiciones meteorológicas"><BarList items={data.entorno?.meteo} total={total} color="#26C6DA" /></Block>
            <Block title="Iluminación"><BarList items={data.entorno?.iluminacion} total={total} color="#FFD600" /></Block>
            <Block title="Señalización"><BarList items={data.entorno?.senalizacion} total={total} color="#66BB6A" /></Block>
            {data.por_puesto?.length > 0 && (
              <Block title="Por puesto de trabajo"><BarList items={data.por_puesto} total={total} color="#FF7043" /></Block>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CadaPasoPanel;
